// src/locales/English/en.privacyPolicy.ts

const privacyPolicy = {
  privacyPolicy: {
    meta: {
      title: "{{appName}} — Privacy Policy",
      closeAria: "Close",
      closeTitle: "Close",
      lastUpdated: "Last updated: {{date}}",
      subtitle:
        "This Privacy Policy explains how {{companyName}} collects, uses, stores, protects, and shares personal data when you use {{appName}}.",
      top: "↑ Top",
      print: "Print / Save this Privacy Policy as PDF",
      contact: "Contact",
      copyright: "© {{year}} {{companyName}}. All rights reserved.",
    },


overview: {
  title: "🔒 Privacy at a Glance",
  introduction: "👋 Introduction",
  controller: "🏢 Who Processes Your Data",
  summary: "🔎 Privacy Overview",
  dataWeCollect: "📊 Data We Process",
  howWeUseData: "⚙️ How We Use Data",
  legalBases: "⚖️ Why We Process Data",
  location: "📍 Location Data",
  publicContent: "🌐 Public Content",
  messaging: "💬 Messaging",
  cookies: "🍪 Cookies & Local Storage",
  sharing: "🤝 Data Sharing",
  transfers: "🌍 International Data Transfers",
  retention: "🗂️ Data Retention",
  deletion: "🗑️ Account Deletion",
  rights: "👤 Your Privacy Rights",
  security: "🔒 Security",
  changes: "📝 Changes",
  contact: "📩 Contact",
},


introduction: {
title: "1. Introduction",
body: [
{
type: "paragraph",
text: "{{appName}} is a social media platform designed to connect, share content, discover places, and interact with people. We value your privacy and are committed to protecting your personal data.",
},
{
type: "bullets",
items: [
"This Privacy Policy explains what personal data we process, how we use it, when we may share it, how long we retain it, and the choices and rights available to you.",
"'Service' refers to {{appName}}, including its website, applications, features, content-sharing functionality, messaging services, and related platform services.",
"'Personal data' means information that identifies, relates to, describes, or can reasonably be linked to an individual.",
],
},
{
type: "paragraph",
text: "By using {{appName}}, you acknowledge that your personal data may be handled as described in this Privacy Policy.",
},
],
}, 



    controller: {
      title: "2. Who Processes Your Data",
      body:
        "{{companyName}} is responsible for the processing of your personal data when using the Service.",
    },
summary: {
  title: "Privacy Overview",
  bullets: [
    "We collect only information needed to reliably operate the {{companyName}} platform.",
    "We do not sell personal data.",
    "Location information is used for location-based features and posting.",
    "Messages are protected using encryption and other security measures.",
    "You can request access to, download, correct, or delete your personal data."
  ],
},


    dataWeCollect: {
      title: "4. Data We Process",
      body:
        "The personal data we process depends on how you use {{appName}} and which features you choose to enable.",

      providedTitle: "Information You Provide",
     providedBullets: [
"Account information: information required to create and manage your account, such as your email address, profile name, and password.",
"Preferences and settings: privacy preferences, consent choices, notification settings, language settings, security settings, and other account preferences you choose to configure.",
"Profile information: information you choose to add to your profile, such as a profile photo, bio, or your location.",
"Content and activity: photos, videos, posts, likes (of posts), follows (following other profiles), blocks and reports (of other users) and other interactions you make through the Service.",
"Communications: messages, feedback, support requests, reports, or other communications you send to us.",
"Age confirmation: confirmation that you are at least 18 years old.",
"Location data: see Section 7 (Location Data)."
],


      automaticTitle: "Information Collected Automatically",
      automaticBullets: [
"Device and technical data: device type, operating system, browser type, app version, language settings, and similar technical information.",
"Log data: IP address, access times, security events, crash reports, and activity necessary to operate, secure, and protect the Service.",
"Usage data: interactions with features, content, notifications, searches, and account activity.",
"Approximate or precise location data: where required to provide location-based features and subject to your device settings and permissions (see 7. Location Data).",
],

     otherUsersTitle: "Information From Other Users",

otherUsersBullets: [
"Other users may interact with your profile, posts, messages, or content.",
"Other users may follow your account, like your content, send you messages, or otherwise interact with you through the Service.",
"Other users may block your account or report your account, content, messages, or activity for review.",
"Reports, complaints, and safety submissions may include information about your account, content, messages shared by a reporting user, or other activity.",
],

minorsDataTitle: "Data of Minors",

minorsDataBullets: [
  "{{appName}} is intended only for individuals who are at least 18 years old.",
  "We do not knowingly collect personal information from children or individuals below the minimum age required to use the Service.",
  "If we become aware that an account was created by a person under the required age, we may suspend, restrict, or remove the account and associated information.",
],
    },

  howWeUseData: {
title: "5. How We Use Your Data",
body:
"We use personal data only for legitimate and specific purposes connected to operating {{appName}}. We strive to minimize the personal data required to operate the platform. We do not sell any personal data.",
bullets: [
"**Account creation and login**: to create, authenticate, manage, and secure your account.",
"**Service operation**: to provide posting, discovery, following, likes, notifications, messaging, and other Service features.",
"**Location features**: to assign city/country labels to posts, support nearby content, and provide location-based functionality.",
"**Discovery features**: to help users discover relevant content, locations, profiles, and activity within the Service.",
"**Safety, moderation, and enforcement**: to review reports, investigate complaints, enforce our Terms and Community Guidelines, detect harmful activity, and take action against abuse, spam, fraud, or policy violations.",
"**Security**: to prevent fraud, unauthorized access, spam, scraping, technical attacks, and other misuse of the Service.",
"**Communication**: to send account, support, legal, safety, and service-related communications.",
"**Service improvement**: to understand how users interact with the Service and improve reliability, usability, functionality, and performance.",
"**Legal compliance**: to comply with applicable laws, legal requests, regulatory obligations, and enforcement requirements.",
],
},

  legalBases: {
title: "6. Why We Process Data",
body:
"Where applicable privacy and data protection laws require a legal basis for processing personal data, we rely on one or more of the following legal bases:",

bullets: [
"**Contract**: to provide, operate, and maintain accounts, messaging, posting, discovery, and other features you request or use.",
"**Consent**: for optional features, permissions, or processing activities where your consent is required.",
"**Legal obligation**: to comply with applicable laws, regulations, legal requests, court orders, and governmental requirements.",
"**Legitimate interests**: to maintain security, prevent abuse, investigate reports, improve the Service, protect users, and operate the platform effectively.",
"**Protection of vital interests**: where necessary to protect the safety, rights, health, or wellbeing of users or other individuals.",
],
},

  location: {
title: "7. Location Data",

body:
"{{appName}} includes location-based features. Location information is used to support content sharing, discovery features, nearby content, and location labels associated with posts.",

bullets: [
"Creating a post requires location access at the time of posting because posts are associated with a city and country label.",
"Location labels refer to city, country, or similar geographic identifiers associated with a post.",
"We use your device location to determine and display the city and country label associated with a post.",
"Location information may be used to support nearby content and discovery features within the Service.",
"Location permissions are requested through your device operating system.",
"If location permissions are denied or disabled, posting content and certain location-based features may not function correctly.",
"We do not use location data for third-party advertising purposes.",
],

note:
"Precise location data is retained only for as long as necessary to provide location-based functionality, maintain security, comply with legal obligations, or support technical operations.",
},

 publicContent: {
title: "8. Public Content",

body:
"{{appName}} is a social platform. Certain information and content you share may be visible to other users depending on how the Service operates and the visibility of the content.",

bullets: [
"Public posts, photos, videos, profile information, and location labels associated with posts may be visible to other users.",
"Other users may view, like, follow, report, or otherwise interact with your public content.",
"Public content may remain visible until deleted or removed from the Service.",
"If content is copied, shared, downloaded, screenshotted, or otherwise stored by other users, we may not be able to remove every external copy.",
"Your profile name, profile photo, and limited profile information may appear in connection with posts, messages, notifications, follows, likes, reports, or other interactions within the Service.",
],
},


messaging: {
  title: "9. Messaging",

  body:
    "{{appName}} includes private messaging features that allow users to communicate directly with one another. We use encryption and other technical and organizational measures designed to protect messages and related data.",

  bullets: [
    "Messages are protected using encryption and other security measures designed to protect message content during storage and transmission.",
    "Encryption keys and related security information may be processed to support secure messaging functionality.",
    "Message-related metadata may include sender and recipient identifiers, conversation identifiers, timestamps, delivery status information, and technical information necessary to operate the messaging service.",    "Users may delete messages they have sent. When a message is deleted in an active chat, it is removed from both participants' view.",
    "If a user deletes their account, conversations may remain available to other participants, but identifying account information may be removed or anonymized.",
    "We retain messaging data only as necessary to operate the service, maintain security, comply with legal obligations, and enforce our policies."
  ],

  note:
    "While we use encryption and security measures to protect messages, no messaging system can guarantee absolute security under all circumstances.",
},

cookies: {
title: "10. Cookies & Local Storage",

body:
"{{appName}} may use cookies, local storage, session storage, authentication tokens, and similar technologies where necessary to provide, secure, maintain, and operate the Service.",

bullets: [
"Authentication technologies may be used to keep you signed in and manage account sessions.",
"Security-related technologies may be used to help prevent fraud, abuse, unauthorized access, and other harmful activity.",
"Preference storage may be used to remember language, display, accessibility, and other user settings.",
"Certain technical storage may be required for core functionality, performance, reliability, and security of the Service.",
"We do not use third-party advertising cookies or similar technologies for third-party advertising purposes."
],
},

   sharing: {
title: "11. Data Sharing",

body:
"We do not sell personal data. We share personal data only where reasonably necessary to operate, secure, protect, support, or legally provide {{appName}}.",

bullets: [
"**Service providers**: trusted providers that support hosting, cloud infrastructure, content delivery, storage, security, communications, analytics, and other operational services.",
"**Legal compliance**: where required by applicable law, legal process, court order, regulatory request, or governmental authority.",
"**Safety and security**: where necessary to protect users, investigate abuse, enforce our policies, prevent fraud, or respond to safety or security risks.",
"**Business transactions**: in connection with a merger, acquisition, financing, reorganization, restructuring, sale of assets, or similar corporate transaction involving {{companyName}}.",
"**With your direction**: when you choose to share content, interact with other users, communicate through the Service, or use public features."
],

noSale:
"{{companyName}} does not sell personal data and does not use personal data for third-party advertising purposes.",
},

transfers: {
title: "12. International Data Transfers",

body:
"Personal data may be processed or stored in countries outside your country of residence, including through the use of cloud infrastructure, hosting providers, and other service providers. Where required by applicable law, we take reasonable steps that personal data receives an appropriate level of protection.",
},


retention: {
title: "13. Data Retention",

body:
"We keep personal data only for as long as necessary for the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law.",

bullets: [
"**Account data**: retained while your account is active and for a limited period after deletion where necessary for legal, security, fraud-prevention, or technical reasons.",
"**Public posts**: retained until deleted by you, removed by us, or deleted with your account, subject to backup retention, legal obligations, or moderation requirements.",
"**Messages**: retained as necessary to provide messaging functionality. Conversations may remain available to other participants unless deleted, anonymized, or technically removed in accordance with the Service's functionality.",
"**Location labels**: city and country labels associated with posts may remain available while the related post remains available.",
"**Security logs**: retained for limited periods to maintain security, investigate incidents, prevent fraud, and protect the Service.",
"**Reports and moderation records**: retained as necessary to review reports, enforce our Terms and Community Guidelines, prevent repeated abuse, and comply with legal obligations.",
"**Backups**: deleted data may remain in backup systems for a limited period before being overwritten, removed, or securely destroyed."
],
},

deletion: {
title: "14. Account Deletion",

body:
"You may request deletion of your account and personal data through the account deletion process provided within the Service. When your account is deleted, associated personal data is generally deleted. Certain information may be retained, anonymized, or disassociated from your account where necessary for legal compliance, security, fraud prevention, dispute resolution, technical operations, or backup retention requirements.",

bullets: [
"Your account, profile information, and access to the Service may be removed, deleted, anonymized, or disabled.",
"Content associated with your account may be deleted, removed, anonymized, or disconnected from your account, subject to technical, legal, security, moderation, or backup requirements.",
"Messages and conversations involving other users may remain available to those users, but information identifying your account may be removed, anonymized, or replaced.",
"Messages can only be removed from both participants' view through the active messaging functionality provided within the Service.",
"Certain records may be retained where necessary for legal compliance, dispute resolution, fraud prevention, abuse prevention, security, or enforcement of our Terms and Community Guidelines.",
"Backup copies may remain for a limited period before being overwritten, removed, or securely destroyed through normal backup retention processes."
],
},

rights: {
title: "15. Your Privacy Rights",

body:
"Depending on your location, applicable law, and the nature of the processing, you may have certain rights regarding your personal data.",

bullets: [
"Right to access personal data we hold about you.",
"Right to correct inaccurate, incomplete, or outdated personal data.",
"Right to request deletion of personal data, subject to applicable legal, security, and operational requirements.",
"Right to restrict or object to certain processing activities.",
"Right to request a copy of your personal data.",
"Right to withdraw consent for optional processing activities, such as certain location permissions or device permissions.",
"Right to request information about how your personal data is processed.",
"Right to lodge a complaint with a competent data protection authority where applicable.",
],

contactPrefix:
"To exercise your privacy rights or ask questions about this Privacy Policy, contact us at",

or: "or",
},

security: {
title: "16. Security",

body:
"We use security measures designed to help protect personal data. However, no method of transmission, storage, or security measure can guarantee absolute security.",

bullets: [
"Security measures may include encryption, authentication controls, monitoring, access restrictions, and other safeguards.",
"Security features and protections may change as the Service evolves.",
"Users are responsible for maintaining the security of their accounts, credentials, devices, and email accounts associated with the Service. Passwords must not be shared and should be kept secret.",
],
},



changes: {
  title: "17. Changes to This Privacy Policy",

  body:
    "We may update this Privacy Policy from time to time to reflect changes to the Service, our practices, legal requirements, or other operational needs. If we make material changes, we may provide notice through the Service, by email, or by other reasonable means where appropriate. The updated version will become effective on the date indicated by the 'Last Updated' date at the top of this Privacy Policy.",

  bullets: [
    "Updates may be made to reflect new features, functionality, products, or services offered through the Service.",
    "We may revise this Privacy Policy to comply with changes in applicable laws, regulations, or regulatory guidance.",
    "Significant changes affecting how we collect, use, disclose, or protect personal data may be highlighted through the Service or other communication channels.",
    "The version displayed on the Service is the current and controlling version of this Privacy Policy.",
    "We encourage you to review this Privacy Policy periodically to remain informed about our privacy practices."
  ],

  note:
    "If you do not agree with a revised version of this Privacy Policy, you may discontinue use of the Service and request deletion of your account and personal data, subject to our legal and regulatory obligations."
},


    contact: {
      title: "18. Contact",
      body:
        "{{companyName}}\n{{companyAddress}}\n{{contactEmail}}",
    },
  },
} as const;

export default privacyPolicy;